/**
 * AudioService
 * 
 * Handles Web Audio API sounds, TTS, and spatial panning for key feedback.
 * Uses advanced procedural synthesis to simulate realistic mechanical switch physics.
 */
export class AudioService {
  constructor() {
    this.audioCtx = null;
    this.masterGain = null;
    this.noiseBuffer = null;
    this.volume = 0.5;
    this.mute = true;
    this.currentPreset = 'thock';
    this.ttsEnabled = false;

    // Presets defining the physical parameters of the switch sound
    this.presets = {
      thock: {
        thudFreq: 120, thudDrop: 40, thudDecay: 0.1,
        noiseFilter: 600, noiseQ: 0.8, noiseDecay: 0.04, noiseVol: 0.4
      },
      creamy: {
        thudFreq: 160, thudDrop: 45, thudDecay: 0.12,
        noiseFilter: 1000, noiseQ: 1.2, noiseDecay: 0.05, noiseVol: 0.35
      },
      clicky: {
        thudFreq: 280, thudDrop: 90, thudDecay: 0.04,
        noiseFilter: 3200, noiseQ: 4.0, noiseDecay: 0.015, noiseVol: 1.0
      },
      tactile: {
        thudFreq: 200, thudDrop: 70, thudDecay: 0.08,
        noiseFilter: 1600, noiseQ: 2.0, noiseDecay: 0.03, noiseVol: 0.6
      },
      blue_switch: { // Cherry MX Blue Clone
        thudFreq: 320, thudDrop: 120, thudDecay: 0.03,
        noiseFilter: 4500, noiseQ: 8.0, noiseDecay: 0.01, noiseVol: 1.8
      },
      brown_switch: { // Cherry MX Brown Clone
        thudFreq: 210, thudDrop: 60, thudDecay: 0.07,
        noiseFilter: 2200, noiseQ: 3.5, noiseDecay: 0.025, noiseVol: 0.7
      },
      red_switch: { // Cherry MX Red Clone
        thudFreq: 150, thudDrop: 30, thudDecay: 0.09,
        noiseFilter: 800, noiseQ: 0.5, noiseDecay: 0.04, noiseVol: 0.3
      },
      typewriter: {
        thudFreq: 400, thudDrop: 150, thudDecay: 0.06,
        noiseFilter: 4000, noiseQ: 5.0, noiseDecay: 0.015, noiseVol: 1.5
      },
      membrane: {
        thudFreq: 80, thudDrop: 15, thudDecay: 0.07,
        noiseFilter: 350, noiseQ: 0.2, noiseDecay: 0.03, noiseVol: 0.15
      },
      silent: {
        thudFreq: 100, thudDrop: 10, thudDecay: 0.04,
        noiseFilter: 300, noiseQ: 0.2, noiseDecay: 0.01, noiseVol: 0.1
      },
      bubble_pop: { // Popping / Bubble sound
        thudFreq: 600, thudDrop: -200, thudDecay: 0.1,
        noiseFilter: 4000, noiseQ: 0.5, noiseDecay: 0.02, noiseVol: 0.4
      },
      bit8: { // Arcade / Retro gaming
        thudFreq: 440, thudDrop: 0, thudDecay: 0.05,
        noiseFilter: 2000, noiseQ: 10.0, noiseDecay: 0.02, noiseVol: 1.2,
        type: 'square'
      },
      laser: { // Cyber / Futuristic
        thudFreq: 880, thudDrop: 600, thudDecay: 0.08,
        noiseFilter: 5000, noiseQ: 1.0, noiseDecay: 0.01, noiseVol: 0.5,
        type: 'sine'
      },
      wooden: { // Nord / Natural
        thudFreq: 180, thudDrop: 20, thudDecay: 0.15,
        noiseFilter: 400, noiseQ: 5.0, noiseDecay: 0.1, noiseVol: 0.6
      },
      steel: { // Carbon / Industrial
        thudFreq: 300, thudDrop: 50, thudDecay: 0.2,
        noiseFilter: 8000, noiseQ: 2.0, noiseDecay: 0.05, noiseVol: 0.8
      }
    };
  }

  /**
   * Automatically select best-fit audio preset for a visual theme
   */
  syncWithTheme(themeName) {
    const themeMap = {
      cyber: 'laser',
      synthwave: 'creamy',
      retro: 'typewriter',
      bubble: 'bubble_pop',
      mac: 'membrane',
      arcade: 'bit8',
      mint: 'red_switch',
      nord: 'wooden',
      dracula: 'blue_switch',
      classic: 'thock',
      carbon: 'steel',
      glass: 'silent',
      flat: 'membrane',
      floating: 'creamy'
    };

    const suggested = themeMap[themeName] || 'thock';
    this.setPreset(suggested);
    return suggested;
  }

  init() {
    if (!this.audioCtx) {
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)({
        latencyHint: 'interactive'
      });
      this.masterGain = this.audioCtx.createGain();
      this.masterGain.connect(this.audioCtx.destination);
      this.setVolume(this.volume);
      this._createNoiseBuffer();
    }

    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
  }

  // Pre-compute a white noise buffer for the "scratch/clatter" of the plastic switch
  _createNoiseBuffer() {
    const bufferSize = this.audioCtx.sampleRate * 0.5; // 0.5 seconds of noise
    this.noiseBuffer = this.audioCtx.createBuffer(1, bufferSize, this.audioCtx.sampleRate);
    const output = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
  }

  _isStabilizerKey(code) {
    return ['Space', 'Enter', 'ShiftLeft', 'ShiftRight', 'Backspace', 'NumpadEnter', 'Numpad0', 'NumpadAdd', 'Tab', 'CapsLock', 'Backslash'].includes(code);
  }

  playKeySound(code, location = 0) {
    this._playPhysicalModel(code, location, false);
  }

  playKeyRelease(code, location = 0) {
    this._playPhysicalModel(code, location, true);
  }

  _playPhysicalModel(code, location, isRelease) {
    if (!this.audioCtx || this.mute) return;

    // Ensure state is running (some browsers suspend even after init)
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }

    const now = this.audioCtx.currentTime;
    const basePreset = this.presets[this.currentPreset] || this.presets['thock'];

    // 1. Organic Randomization (Jitter) so no two keystrokes sound identical
    let pitchJitter = 1.0 + (Math.random() * 0.06 - 0.03); // +/- 3% pitch variance
    let volJitter = 1.0 + (Math.random() * 0.1 - 0.05);   // +/- 5% volume variance

    // 2. Stabilizer Physics Modifications
    // Spacebars/Enters are acoustically larger cavities: deeper pitch, longer decay
    let thudMod = 1.0;
    let decayMod = 1.0;
    let noiseFilterMod = 1.0;

    if (code && this._isStabilizerKey(code)) {
      thudMod = 0.95;      // Only slightly lower pitch (prevent falling below laptop speaker threshold)
      decayMod = 2.5;      // Much longer resonance (larger cavity simulates size better than sub-bass)
      noiseFilterMod = 0.7; // Darker scratch (damps the high frequencies)
      volJitter *= 2.0;    // Stabilizers need significantly more gain
    }

    // 3. Release (Top Out) Physics Modifications
    // When the key bounces back up, the stem hits the top housing. Less bass, higher clack, quieter.
    if (isRelease) {
      thudMod *= 1.4;      // 40% higher pitch on release
      decayMod *= 0.5;     // 50% shorter
      noiseFilterMod *= 1.2;
      volJitter *= 0.6;    // Release is quieter than bottom out
    }

    // Apply combined modifiers
    const preset = {
      thudFreq: basePreset.thudFreq * thudMod * pitchJitter,
      thudDrop: basePreset.thudDrop * thudMod * pitchJitter,
      thudDecay: basePreset.thudDecay * decayMod,
      noiseFilter: basePreset.noiseFilter * noiseFilterMod * pitchJitter,
      noiseQ: basePreset.noiseQ,
      noiseDecay: basePreset.noiseDecay * decayMod,
      noiseVol: basePreset.noiseVol * volJitter
    };

    // 4. Setup Master Panning for this keystroke
    const panner = this.audioCtx.createStereoPanner();
    let xPos = 0;
    if (location === 1) xPos = -0.5; // Left modifiers
    if (location === 2) xPos = 0.5;  // Right modifiers
    if (location === 3) xPos = 0.3;  // Numpad

    // Add micro-panning for alpha keys roughly based on QWERTY layout
    if (location === 0 && code && code.startsWith('Key')) {
      if (['KeyQ', 'KeyA', 'KeyZ', 'KeyW', 'KeyS', 'KeyX'].includes(code)) xPos = -0.3;
      if (['KeyP', 'KeyL', 'KeyM', 'KeyO', 'KeyK'].includes(code)) xPos = 0.3;
    }

    panner.pan.value = xPos;
    panner.connect(this.masterGain);

    // 2. The "Thud" / Resonance (Oscillator)
    const osc = this.audioCtx.createOscillator();
    const oscGain = this.audioCtx.createGain();

    osc.type = preset.type || 'sine';

    // Pitch envelope: drops rapidly to simulate physical impact. Safe min bounds for exponential ramp.
    const startFreq = Math.max(1, preset.thudFreq + preset.thudDrop);
    const endFreq = Math.max(1, preset.thudFreq);
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.exponentialRampToValueAtTime(endFreq, now + 0.02);

    // Oscillator volume controlled by volJitter (ceiling raised from 1.2 to 2.5 for stabilizers)
    oscGain.gain.setValueAtTime(0, now);
    oscGain.gain.linearRampToValueAtTime(Math.min(2.5, volJitter), now + 0.002);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + preset.thudDecay);

    osc.connect(oscGain);
    oscGain.connect(panner);

    // 3. The "Scratch" / Click (Filtered White Noise)
    let noiseSrc = null;
    let noiseGain = null;
    let noiseFilter = null;

    if (this.noiseBuffer) {
      noiseSrc = this.audioCtx.createBufferSource();
      noiseSrc.buffer = this.noiseBuffer;

      noiseFilter = this.audioCtx.createBiquadFilter();
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = preset.noiseFilter;
      noiseFilter.Q.value = preset.noiseQ;

      noiseGain = this.audioCtx.createGain();
      noiseGain.gain.setValueAtTime(0, now);
      noiseGain.gain.linearRampToValueAtTime(preset.noiseVol, now + 0.001);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, now + preset.noiseDecay);

      noiseSrc.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(panner);
    }

    // Start & Stop
    osc.start(now);
    osc.stop(now + preset.thudDecay + 0.05);

    if (noiseSrc) {
      noiseSrc.start(now);
      noiseSrc.stop(now + preset.noiseDecay + 0.05);
    }
  }

  speak(text) {
    if (!this.ttsEnabled) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.2;
    utterance.pitch = 1.0;
    window.speechSynthesis.speak(utterance);
  }

  setPreset(name) {
    if (this.presets[name]) {
      this.currentPreset = name;
    }
  }

  setVolume(v) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.masterGain) {
      // Small ramp to prevent crackling on volume change
      this.masterGain.gain.setTargetAtTime(this.volume, this.audioCtx.currentTime, 0.05);
    }
  }

  setMuted(value) {
    this.mute = Boolean(value);
    return this.mute;
  }

  toggleMute() {
    this.mute = !this.mute;
    return this.mute;
  }

  toggleTTS() {
    this.ttsEnabled = !this.ttsEnabled;
    return this.ttsEnabled;
  }
}

export const audioService = new AudioService();
